import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, Input, Checkbox, Button, cn } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { GripVertical, Trash2, Plus } from "lucide-react";
import type { AppointmentListBlock, AppointmentCategory } from "../../../../../shared/schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import { ItemFilterSection } from "../shared/ItemFilterSection";
import type { BlockTypeEditorProps } from "../types";
import type { VisibilityCondition } from "../../../../../shared/schema/visibility";
import { arrayMove } from "../utils";
import { APPOINTMENT_CATEGORY_PREFERRED_ORDER, getAppointmentCategoryInfo } from "../constants";

type AppointmentListBlockEditorProps = BlockTypeEditorProps<AppointmentListBlock>;

interface OverrideRow {
  id: string;
  title: string;
  url: string;
}

interface RescheduleUrlOverridesSectionProps {
  rescheduleUrlOverrides: Record<string, string> | undefined;
  onChange: (overrides: Record<string, string> | undefined) => void;
}

const RescheduleUrlOverridesSection = ({ rescheduleUrlOverrides, onChange }: RescheduleUrlOverridesSectionProps) => {
  const { t } = useTranslation("portal");
  const idCounter = useRef(0);

  const [rows, setRows] = useState<OverrideRow[]>(() =>
    Object.entries(rescheduleUrlOverrides ?? {}).map(([title, url]) => ({
      id: String(idCounter.current++),
      title,
      url,
    }))
  );

  const applyRows = (newRows: OverrideRow[]) => {
    setRows(newRows);
    const record = Object.fromEntries(newRows.filter((r) => r.title).map((r) => [r.title, r.url]));
    onChange(Object.keys(record).length > 0 ? record : undefined);
  };

  const addRow = () => {
    applyRows([...rows, { id: String(idCounter.current++), title: "", url: "" }]);
  };

  const updateRow = (id: string, field: "title" | "url", value: string) => {
    applyRows(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const removeRow = (id: string) => {
    applyRows(rows.filter((r) => r.id !== id));
  };

  return (
    <div className="space-y-2">
      <Label>{t("Per-Appointment Reschedule URLs (optional)")}</Label>
      <p className="text-xs text-muted-foreground">
        {t(
          "Set custom reschedule URLs for specific appointment types. Takes precedence over the global reschedule URL."
        )}
      </p>

      {rows.length > 0 && (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.id} className="flex items-center gap-2">
              <Input
                value={row.title}
                onChange={(e) => updateRow(row.id, "title", e.target.value)}
                placeholder={t("Appointment title")}
                className="flex-1"
              />
              <span className="text-muted-foreground">→</span>
              <Input
                value={row.url}
                onChange={(e) => updateRow(row.id, "url", e.target.value)}
                placeholder="https://booking.example.com/reschedule"
                className="flex-1"
              />
              <Button variant="ghost" size="sm" onClick={() => removeRow(row.id)} className="flex-shrink-0">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button variant="outline" size="sm" onClick={addRow} className="w-full">
        <Plus className="h-4 w-4 mr-1" />
        {t("Add Reschedule URL Override")}
      </Button>
    </div>
  );
};

export function AppointmentListBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: AppointmentListBlockEditorProps) {
  const { t } = useTranslation("portal");
  const categories = block.props.categories ?? ["all"];

  // Drag state for category reordering
  const [draggedCategory, setDraggedCategory] = useState<AppointmentCategory | null>(null);

  // Video hide time — store display value and unit separately, convert to minutes for schema
  type VideoHideUnit = "minutes" | "hours" | "days";
  const deriveUnit = (minutes: number | undefined): VideoHideUnit => {
    if (minutes === undefined) return "minutes";
    if (minutes % (60 * 24) === 0) return "days";
    if (minutes % 60 === 0) return "hours";
    return "minutes";
  };
  const toDisplayValue = (minutes: number | undefined, unit: VideoHideUnit): string => {
    if (minutes === undefined) return "";
    if (unit === "days") return String(minutes / (60 * 24));
    if (unit === "hours") return String(minutes / 60);
    return String(minutes);
  };
  const [videoHideUnit, setVideoHideUnit] = useState<VideoHideUnit>(() =>
    deriveUnit(block.props.hideVideoAfterMinutes)
  );
  const [videoHideValue, setVideoHideValue] = useState<string>(() =>
    toDisplayValue(block.props.hideVideoAfterMinutes, deriveUnit(block.props.hideVideoAfterMinutes))
  );
  const toMinutes = (value: string, unit: VideoHideUnit): number | undefined => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 0) return undefined;
    if (unit === "hours") return parsed * 60;
    if (unit === "days") return parsed * 60 * 24;
    return parsed;
  };

  const toggleCategory = (category: AppointmentCategory, checked: boolean) => {
    let newCategories: AppointmentCategory[];
    if (checked) {
      // Add category - insert in preferred order position relative to existing categories
      const existingIndices = categories.map((c) => APPOINTMENT_CATEGORY_PREFERRED_ORDER.indexOf(c));
      const newIndex = APPOINTMENT_CATEGORY_PREFERRED_ORDER.indexOf(category);
      // Find where to insert based on preferred order
      let insertAt = categories.length;
      for (let i = 0; i < categories.length; i++) {
        const existingIndex = existingIndices[i] ?? -1;
        if (existingIndex > newIndex) {
          insertAt = i;
          break;
        }
      }
      newCategories = [...categories.slice(0, insertAt), category, ...categories.slice(insertAt)];
    } else {
      // Remove category, but ensure at least one remains
      newCategories = categories.filter((c) => c !== category);
      if (newCategories.length === 0) {
        newCategories = ["all"]; // Fallback to "all" if nothing selected
      }
    }
    updateProps({ categories: newCategories });
  };

  const handleDragStart = (category: AppointmentCategory) => {
    setDraggedCategory(category);
  };

  const handleDragOver = (e: React.DragEvent, targetCategory: AppointmentCategory) => {
    e.preventDefault();
    if (!draggedCategory || draggedCategory === targetCategory) return;

    const fromIndex = categories.indexOf(draggedCategory);
    const toIndex = categories.indexOf(targetCategory);
    if (fromIndex === -1 || toIndex === -1) return;

    const newCategories = arrayMove(categories, fromIndex, toIndex);
    updateProps({ categories: newCategories });
  };

  const handleDragEnd = () => {
    setDraggedCategory(null);
  };

  // Get unselected categories for the "add" section
  const unselectedCategories = APPOINTMENT_CATEGORY_PREFERRED_ORDER.filter((c) => !categories.includes(c));

  const updateItemFilter = (condition: VisibilityCondition | undefined) => {
    if (condition) {
      updateProps({ itemFilter: { condition } });
    } else {
      updateProps({ itemFilter: undefined });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Categories")}</Label>
        <p className="text-xs text-muted-foreground">
          {t("Select which appointment categories to show. Multiple selections will show a filter.")}
        </p>

        {/* Selected categories - draggable for reordering */}
        {categories.length > 0 && (
          <div className="space-y-1 pt-1">
            <p className="text-xs text-muted-foreground font-medium">{t("Selected (drag to reorder)")}</p>
            {categories.map((category) => {
              const info = getAppointmentCategoryInfo()[category];
              const isDragging = draggedCategory === category;
              return (
                <div
                  key={category}
                  draggable={categories.length > 1}
                  onDragStart={() => handleDragStart(category)}
                  onDragOver={(e) => handleDragOver(e, category)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    "flex items-center gap-2 p-1.5 rounded border bg-background",
                    categories.length > 1 && "cursor-grab active:cursor-grabbing",
                    isDragging && "opacity-50"
                  )}
                >
                  {categories.length > 1 && <GripVertical className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
                  <Checkbox
                    id={`cat-${category}`}
                    checked={true}
                    onCheckedChange={() => toggleCategory(category, false)}
                  />
                  <label htmlFor={`cat-${category}`} className="text-sm cursor-pointer flex-1">
                    {info.label}
                    {info.description && (
                      <span className="text-xs text-muted-foreground ml-1">({info.description})</span>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        )}

        {/* Unselected categories - click to add */}
        {unselectedCategories.length > 0 && (
          <div className="space-y-1 pt-2">
            <p className="text-xs text-muted-foreground font-medium">{t("Available")}</p>
            {unselectedCategories.map((category) => {
              const info = getAppointmentCategoryInfo()[category];
              return (
                <div key={category} className="flex items-center gap-2 p-1.5 rounded border border-dashed">
                  <Checkbox
                    id={`cat-add-${category}`}
                    checked={false}
                    onCheckedChange={() => toggleCategory(category, true)}
                  />
                  <label
                    htmlFor={`cat-add-${category}`}
                    className="text-sm cursor-pointer text-muted-foreground flex-1"
                  >
                    {info.label}
                    {info.description && (
                      <span className="text-xs text-muted-foreground ml-1">({info.description})</span>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label>{t("Max Items to Show")}</Label>
        <Select
          value={String(block.props.maxItems ?? 3)}
          onValueChange={(value) => updateProps({ maxItems: parseInt(value, 10) })}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="1">1</Select.Item>
            <Select.Item value="2">2</Select.Item>
            <Select.Item value="3">3</Select.Item>
            <Select.Item value="5">5</Select.Item>
            <Select.Item value="10">10</Select.Item>
          </Select.Content>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t("Empty Message (optional)")}</Label>
        <Input
          value={block.props.emptyMessage || ""}
          onChange={(e) => updateProps({ emptyMessage: e.target.value || undefined })}
          placeholder={t("No appointments")}
        />
      </div>

      {/* Cancel/Reschedule Settings */}
      <div className="border-t pt-4 mt-4">
        <Label className="text-sm font-medium">{t("Appointment Actions")}</Label>
        <p className="text-xs text-muted-foreground mb-3">
          {t("Enable actions that users can perform on their appointments.")}
        </p>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="allowCancelAppointment"
              checked={block.props.allowCancel ?? false}
              onCheckedChange={(checked) => updateProps({ allowCancel: !!checked })}
            />
            <label htmlFor="allowCancelAppointment" className="text-sm cursor-pointer">
              {t("Allow Cancel Appointment")}
            </label>
          </div>
          <p className="text-xs text-muted-foreground ml-6">
            {t("Users can cancel upcoming appointments. Requires confirmation.")}
          </p>

          <div className="space-y-2">
            <Label>{t("Custom Reschedule URL (optional)")}</Label>
            <Input
              value={block.props.rescheduleUrl || ""}
              onChange={(e) => updateProps({ rescheduleUrl: e.target.value || undefined })}
              placeholder="https://booking.example.com/reschedule?patient={{enduser.fname}}"
            />
            <p className="text-xs text-muted-foreground">
              {t(
                "If set, a Reschedule button will appear after an appointment is cancelled. Use template variables like {{enduser.fname}}, {{enduser.lname}}, {{enduser.email}}, {{snippet.key}}."
              )}
            </p>
          </div>

          <RescheduleUrlOverridesSection
            rescheduleUrlOverrides={block.props.rescheduleUrlOverrides}
            onChange={(overrides) => updateProps({ rescheduleUrlOverrides: overrides })}
          />
        </div>
      </div>

      {/* Video Call Settings */}
      <div className="border-t pt-4 mt-4">
        <Label className="text-sm font-medium">{t("Video Call Settings")}</Label>
        <p className="text-xs text-muted-foreground mb-3">
          {t("Control when the video call button is visible on appointments.")}
        </p>
        <div className="space-y-2">
          <Label>{t("Hide video call button after appointment ends")}</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              min={0}
              className="flex-1"
              value={videoHideValue}
              onChange={(e) => {
                setVideoHideValue(e.target.value);
                updateProps({ hideVideoAfterMinutes: toMinutes(e.target.value, videoHideUnit) });
              }}
              placeholder={t("No limit")}
            />
            <Select
              value={videoHideUnit}
              onValueChange={(unit: VideoHideUnit) => {
                setVideoHideUnit(unit);
                updateProps({ hideVideoAfterMinutes: toMinutes(videoHideValue, unit) });
              }}
            >
              <Select.Trigger className="w-28">
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                <Select.Item value="minutes">{t("Minutes")}</Select.Item>
                <Select.Item value="hours">{t("Hours")}</Select.Item>
                <Select.Item value="days">{t("Days")}</Select.Item>
              </Select.Content>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            {t(
              "If set, the video call button will be hidden after this time period following the appointment end. Leave blank to always show the button."
            )}
          </p>
        </div>
      </div>

      <ItemFilterSection
        itemFilter={block.props.itemFilter}
        onChange={updateItemFilter}
        description={t(
          "Filter which appointments are displayed based on conditions. Use 'Appointments' source for appointment properties."
        )}
      />

      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
